import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Croissant, Plus, Search, Trash2 } from 'lucide-react';
import { formatMoney, type ProductWithDetail } from '@bakery/shared';

import { api } from '../lib/api.js';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Modal,
  SectionHeading,
  Select,
  Skeleton,
  cx,
  useToast,
} from '../components/ui.js';

export function CatalogScreen() {
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<ProductWithDetail | null>(null);
  const [creating, setCreating] = useState(false);

  const products = useQuery({
    queryKey: ['products', 'detailed', search],
    queryFn: () => api.catalog.products({ search: search || undefined }),
  });

  return (
    <div className="mx-auto max-w-5xl px-4 py-5">
      <header className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold">Products &amp; recipes</h1>
          <p className="mt-0.5 text-sm text-[var(--color-ink-soft)]">
            Bind ingredients to each product and the true cost per loaf follows automatically.
          </p>
        </div>
        <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
          New product
        </Button>
      </header>

      <div className="relative mb-4">
        <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-[var(--color-ink-faint)]" />
        <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Find a product…" className="pl-9" />
      </div>

      {products.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} className="h-16" />
          ))}
        </div>
      ) : products.data?.length === 0 ? (
        <EmptyState icon={<Croissant className="size-8" />} title="No products" description="Add what you sell to fill the register." />
      ) : (
        <ul className="space-y-1.5">
          {products.data?.map((product) => (
            <li key={product.id}>
              <button onClick={() => setEditing(product)} className="card flex w-full items-center gap-4 px-4 py-3 text-left transition hover:border-crust-400">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{product.name}</p>
                  <p className="truncate text-xs text-[var(--color-ink-faint)]">
                    {product.sku}
                    {product.category && ` · ${product.category.name}`}
                    {product.batchYield > 1 && ` · ${product.batchYield} per batch`}
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="tabular text-sm font-semibold">{formatMoney(product.price)}</p>
                  <p className="tabular text-xs text-[var(--color-ink-faint)]">
                    {typeof product.computedCost === 'number' ? `costs ${formatMoney(product.computedCost)}` : 'no recipe'}
                  </p>
                </div>
                {typeof product.marginPercent === 'number' && (
                  <Badge tone={product.marginPercent > 55 ? 'ok' : product.marginPercent > 35 ? 'warn' : 'danger'}>
                    {product.marginPercent}%
                  </Badge>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      {editing && <ProductModal product={editing} onClose={() => setEditing(null)} />}
      {creating && <ProductModal onClose={() => setCreating(false)} />}
    </div>
  );
}

function ProductModal({ product, onClose }: { product?: ProductWithDetail; onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const categories = useQuery({ queryKey: ['categories'], queryFn: api.catalog.categories });
  const ingredients = useQuery({ queryKey: ['ingredients'], queryFn: api.inventory.ingredients });
  const units = useQuery({ queryKey: ['units'], queryFn: api.inventory.units });
  const recipe = useQuery({
    queryKey: ['recipe', product?.id],
    queryFn: () => api.catalog.recipe(product!.id),
    enabled: Boolean(product),
  });

  const [form, setForm] = useState({
    name: product?.name ?? '',
    sku: product?.sku ?? '',
    categoryId: product?.categoryId ?? '',
    price: product?.price ?? 0,
    batchYield: product?.batchYield ?? 1,
    description: product?.description ?? '',
  });

  const [lines, setLines] = useState<{ ingredientId: string; qty: number; unit: string; wastePercent: number }[]>([]);
  const [linesLoaded, setLinesLoaded] = useState(false);

  // Seed the editor once the saved recipe arrives.
  if (recipe.data && !linesLoaded) {
    setLines(
      (recipe.data.lines as { ingredientId: string; qty: number; unit: string; wastePercent: number }[]).map((line) => ({
        ingredientId: line.ingredientId,
        qty: line.qty,
        unit: line.unit,
        wastePercent: line.wastePercent,
      })),
    );
    setLinesLoaded(true);
  }

  const save = useMutation({
    mutationFn: async () => {
      const body = { ...form, categoryId: form.categoryId || null, price: form.price, batchYield: Number(form.batchYield) };
      const saved = product ? await api.catalog.updateProduct(product.id, body) : await api.catalog.createProduct(body);
      if (lines.length > 0 || product) await api.catalog.saveRecipe(saved.id, lines);
      return saved;
    },
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['recipe'] });
      toast.push({ tone: 'ok', title: `${saved.name} saved` });
      onClose();
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not save', body: error.message }),
  });

  const remove = useMutation({
    mutationFn: () => api.catalog.deleteProduct(product!.id),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.push({
        tone: 'ok',
        title: result.action === 'archived' ? 'Product archived' : 'Product removed',
        body: result.action === 'archived' ? 'It has been sold before, so its sales history is kept.' : undefined,
      });
      onClose();
    },
  });

  const cost = recipe.data?.cost;
  const margin = cost && form.price > 0 ? Math.round(((form.price - cost.costPerUnit) / form.price) * 1000) / 10 : null;

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={product ? product.name : 'New product'}
      footer={
        <>
          {product && (
            <Button variant="ghost" icon={<Trash2 className="size-4" />} loading={remove.isPending} onClick={() => remove.mutate()}>
              Delete
            </Button>
          )}
          <div className="flex-1" />
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} disabled={!form.name} onClick={() => save.mutate()}>
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Name" required>
            <Input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} autoFocus />
          </Field>
          <Field label="Category">
            <Select value={form.categoryId} onChange={(event) => setForm({ ...form, categoryId: event.target.value })}>
              <option value="">None</option>
              {categories.data?.map((category) => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </Select>
          </Field>
          <Field label="Selling price">
            <Input
              type="number"
              inputMode="decimal"
              value={form.price / 100}
              onChange={(event) => setForm({ ...form, price: Math.round(Number(event.target.value) * 100) })}
              className="tabular text-right"
            />
          </Field>
          <Field label="Units per batch" hint="The recipe below makes this many.">
            <Input
              type="number"
              min={1}
              value={form.batchYield}
              onChange={(event) => setForm({ ...form, batchYield: Number(event.target.value) })}
              className="tabular text-right"
            />
          </Field>
        </div>

        {/* Recipe ------------------------------------------------------- */}
        <section>
          <SectionHeading
            title="Recipe"
            hint="Quantities are per batch. Waste covers what stays in the mixer."
            action={
              cost && (
                <div className="text-right text-sm">
                  <p className="tabular font-semibold">{formatMoney(cost.costPerUnit)} / unit</p>
                  {margin !== null && <p className="text-xs text-[var(--color-ink-faint)]">{margin}% margin</p>}
                </div>
              )
            }
          />

          {cost && !cost.isComplete && (
            <p className="mb-2 rounded-[var(--radius-md)] bg-[var(--color-warn-soft)] px-3 py-2 text-xs text-[var(--color-warn)]">
              Some ingredients have never been purchased, so this cost is an underestimate. Record a delivery to price them.
            </p>
          )}

          <div className="space-y-2">
            {lines.map((line, index) => {
              const ingredient = ingredients.data?.find((i) => i.id === line.ingredientId);
              const compatible = units.data?.byDimension[ingredient?.dimension ?? 'mass'] ?? [];

              return (
                <div key={index} className="grid grid-cols-12 items-center gap-2">
                  <div className="col-span-5">
                    <Select
                      value={line.ingredientId}
                      onChange={(event) => {
                        const next = ingredients.data?.find((i) => i.id === event.target.value);
                        setLines((current) =>
                          current.map((l, i) =>
                            i === index ? { ...l, ingredientId: event.target.value, unit: next?.stockUnit ?? l.unit } : l,
                          ),
                        );
                      }}
                    >
                      {ingredients.data?.map((option) => (
                        <option key={option.id} value={option.id}>{option.name}</option>
                      ))}
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Input
                      type="number"
                      inputMode="decimal"
                      value={line.qty}
                      onChange={(event) => setLines((c) => c.map((l, i) => (i === index ? { ...l, qty: Number(event.target.value) } : l)))}
                      className="tabular text-right"
                    />
                  </div>
                  <div className="col-span-2">
                    <Select
                      value={line.unit}
                      onChange={(event) => setLines((c) => c.map((l, i) => (i === index ? { ...l, unit: event.target.value } : l)))}
                    >
                      {compatible.map((unit) => (
                        <option key={unit.code} value={unit.code}>{unit.code}</option>
                      ))}
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Input
                      type="number"
                      inputMode="decimal"
                      value={line.wastePercent}
                      onChange={(event) => setLines((c) => c.map((l, i) => (i === index ? { ...l, wastePercent: Number(event.target.value) } : l)))}
                      className="tabular text-right"
                      title="Waste %"
                    />
                  </div>
                  <button className="col-span-1" onClick={() => setLines((c) => c.filter((_, i) => i !== index))} aria-label="Remove ingredient">
                    <Trash2 className="size-4 text-[var(--color-ink-faint)] hover:text-[var(--color-danger)]" />
                  </button>
                </div>
              );
            })}

            <Button
              size="sm"
              icon={<Plus className="size-4" />}
              disabled={!ingredients.data?.length}
              onClick={() =>
                setLines((current) => [
                  ...current,
                  { ingredientId: ingredients.data![0]!.id, qty: 1, unit: ingredients.data![0]!.stockUnit, wastePercent: 0 },
                ])
              }
            >
              Add an ingredient
            </Button>
          </div>
        </section>
      </div>
    </Modal>
  );
}
